import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import type { Updater } from "@tanstack/vue-table";
import type { Ref } from "vue";

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === "function" ? updaterOrValue(ref.value) : updaterOrValue;
}

/**
 * Compare two values and return true if they are equal
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns True if the values are equal, false otherwise
 *
 * @example
 * ```ts
 * compareValues(1, 1); // true
 * compareValues(1, "1"); // Error: Both values must have the same type
 * compareValues("1", "1"); // true
 * compareValues(1, "2"); // Error: Both values must have the same type
 * compareValues("1", 2); // Error: Both values must have the same type
 * ```
 */
export function compareValues(a: number | string | null | undefined, b: number | string | null | undefined): boolean {
  // check if both values are undefined
  if (a === undefined && b === undefined) {
    // throw new Error("Both values must be defined");
    console.error("Both values must be defined");
    return false;
  }

  // check if both values are null
  if (a === null && b === null) {
    // throw new Error("Both values must be defined");
    console.error("Both values must be defined");
    return false;
  }

  // console.debug("check data type", { a, b });

  // check if both values are undefined or null
  if (a === undefined || b === undefined || a === null || b === null) {
    // throw new Error("Both values must be defined");
    console.error("Both values must be defined");
    return false;
  }
  // check if both values have not same type
  if (typeof a !== typeof b) {
    // throw new Error("Both values must have the same type");
    console.error("Both values must have the same type");
    return false;
  }

  if (typeof a === "string" || typeof b === "string") {
    return a.toString() !== b.toString();
  }

  return a !== b;
}
